/**
 * Minimap — XZ top-down HTML Canvas 2D overlay.
 * Shows planets, stars, wormholes, players (fog-aware), ghosts, projectile, camera direction.
 *
 * 37.10: units sit on the rim of their planet's disc in their compass direction,
 * so the map says *where on the planet* you are, not just which planet; the
 * viewer's unit is unmistakable (bright, ringed, with a heading tick) and the
 * acting teammate pulses. Probe / flyover intel shows as a team-coloured hollow
 * ring on the rim of the planet the enemy was seen on, fading with age and
 * dashed once stale; your own spotted units get a red dashed ring. Dots and
 * rings only — no names. The placement math is exported and unit-tested.
 */

import type { GameState, Vec3 } from '../types';
import { TEAMS, WEAPONS, STAR_TYPES } from '../constants';
import type { StarType } from '../types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface MinimapState {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  container: HTMLDivElement;
  size: number;
  padding: number;
}

interface Bounds {
  centerX: number;
  centerZ: number;
  halfExtent: number;
}

// ---------------------------------------------------------------------------
// Init / Dispose / Visibility
// ---------------------------------------------------------------------------

export function initMinimap(size = 180): MinimapState {
  const container = document.createElement('div');
  container.id = 'minimap-container';
  container.style.width = size + 'px';
  container.style.height = size + 'px';

  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  container.appendChild(canvas);

  document.body.appendChild(container);

  const ctx = canvas.getContext('2d')!;
  return { canvas, ctx, container, size, padding: 12 };
}

export function disposeMinimap(mm: MinimapState): void {
  mm.container.remove();
}

export function setMinimapVisible(mm: MinimapState, visible: boolean): void {
  mm.container.style.display = visible ? 'block' : 'none';
}

// ---------------------------------------------------------------------------
// Coordinate mapping
// ---------------------------------------------------------------------------

export function computeBounds(planets: GameState['planets']): Bounds {
  let minX = Infinity, maxX = -Infinity;
  let minZ = Infinity, maxZ = -Infinity;
  for (const p of planets) {
    const r = p.radius;
    if (p.position.x - r < minX) minX = p.position.x - r;
    if (p.position.x + r > maxX) maxX = p.position.x + r;
    if (p.position.z - r < minZ) minZ = p.position.z - r;
    if (p.position.z + r > maxZ) maxZ = p.position.z + r;
  }
  const centerX = (minX + maxX) / 2;
  const centerZ = (minZ + maxZ) / 2;
  const spanX = maxX - minX;
  const spanZ = maxZ - minZ;
  const halfExtent = Math.max(spanX, spanZ) / 2 * 1.1; // 10% margin
  return { centerX, centerZ, halfExtent };
}

function worldToMinimap(
  worldX: number, worldZ: number,
  bounds: Bounds, canvasSize: number, padding: number
): { x: number; y: number } {
  const usable = canvasSize - padding * 2;
  const x = padding + ((worldX - bounds.centerX) / (bounds.halfExtent * 2) + 0.5) * usable;
  const y = padding + ((worldZ - bounds.centerZ) / (bounds.halfExtent * 2) + 0.5) * usable;
  return { x, y };
}

/** Planet disc radius in pixels: big enough that a unit's position on the rim reads (3–14 px). */
export function worldRadiusToPixels(radius: number, bounds: Bounds, canvasSize: number, padding: number): number {
  const usable = canvasSize - padding * 2;
  const px = (radius / (bounds.halfExtent * 2)) * usable;
  return Math.max(3, Math.min(14, px));
}
/**
 * Where a surface direction lands on a planet's disc: on the rim, in the XZ
 * compass direction of the unit. At the poles (direction nearly all Y) there is
 * no compass direction — the dot sits at the disc centre and `atPole` is set so
 * the caller can mark it.
 */
export function surfaceDotOnDisc(discX: number, discY: number, discR: number, surfaceDir: Vec3): { x: number; y: number; atPole: boolean } {
  const hx = surfaceDir.x, hz = surfaceDir.z;
  const h = Math.hypot(hx, hz);
  if (h < 0.15) return { x: discX, y: discY, atPole: true };
  return { x: discX + (hx / h) * discR, y: discY + (hz / h) * discR, atPole: false };
}
/** Intel ages: fresh marks are solid, old ones fade to a quarter by STALE_TURNS. */
export const GHOST_STALE_TURNS = 8;
export function ghostAlpha(turnsAgo: number): number {
  const t = Math.max(0, Math.min(1, turnsAgo / GHOST_STALE_TURNS));
  return 0.85 - 0.6 * t;
}
/** The planet a world position belongs to (nearest centre, by surface distance). */
export function nearestPlanetIndex(pos: Vec3, planets: GameState['planets']): number {
  let best = -1, bestD = Infinity;
  for (let i = 0; i < planets.length; i++) {
    const p = planets[i];
    if (p.type !== 'planet') continue;
    const d = Math.hypot(pos.x - p.position.x, pos.y - p.position.y, pos.z - p.position.z) - p.radius;
    if (d < bestD) { bestD = d; best = i; }
  }
  return best;
}


// ---------------------------------------------------------------------------
// Main update
// ---------------------------------------------------------------------------

export function updateMinimap(
  mm: MinimapState,
  gameState: GameState,
  cameraTheta: number,
  visibleEnemyIds: Set<number>,
  viewerIndex: number,
  fogCam: boolean,
): void {
  const { ctx, size, padding } = mm;
  const { planets, players, projectile, fog } = gameState;

  if (planets.length === 0) return;

  const bounds = computeBounds(planets);

  // 1. Background
  ctx.clearRect(0, 0, size, size);
  ctx.fillStyle = 'rgba(12, 12, 24, 0.75)';
  roundRect(ctx, 0, 0, size, size, 10);
  ctx.fill();
  ctx.strokeStyle = 'rgba(160, 196, 184, 0.2)';
  ctx.lineWidth = 1;
  roundRect(ctx, 0.5, 0.5, size - 1, size - 1, 10);
  ctx.stroke();

  // 2. Stars
  for (const p of planets) {
    if (p.type !== 'star') continue;
    const pos = worldToMinimap(p.position.x, p.position.z, bounds, size, padding);
    const r = worldRadiusToPixels(p.radius, bounds, size, padding);
    const coreColor = p.starType ? STAR_TYPES[p.starType as StarType].coreColor : '#fff4e0';
    // Glow halo
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, r + 3, 0, Math.PI * 2);
    ctx.fillStyle = hexToRgba(coreColor, 0.2);
    ctx.fill();
    // Core
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, r, 0, Math.PI * 2);
    ctx.fillStyle = coreColor;
    ctx.fill();
  }

  // 3. Wormholes
  for (const p of planets) {
    if (p.type !== 'wormhole') continue;
    const pos = worldToMinimap(p.position.x, p.position.z, bounds, size, padding);
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, 3.5, 0, Math.PI * 2);
    ctx.strokeStyle = '#7744dd';
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }

  // 4. Planets — disc plus a faint rim so dots on the rim read as "on the surface"
  for (const p of planets) {
    if (p.type !== 'planet') continue;
    const pos = worldToMinimap(p.position.x, p.position.z, bounds, size, padding);
    const r = worldRadiusToPixels(p.radius, bounds, size, padding);
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, r, 0, Math.PI * 2);
    ctx.fillStyle = p.alive ? p.palette.a : '#2a2a30';
    ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,0.12)';
    ctx.lineWidth = 1;
    ctx.stroke();
  }
  const now = performance.now() / 1000;
  /** Pixel position for a unit: on its planet's rim in its compass direction (airborne: straight projection). */
  const unitDot = (p: { planetIndex: number; surfaceDir: Vec3; airborne: boolean; airbornePos: Vec3 | null }): { x: number; y: number; atPole: boolean } | null => {
    if (p.airborne && p.airbornePos) { const q = worldToMinimap(p.airbornePos.x, p.airbornePos.z, bounds, size, padding); return { x: q.x, y: q.y, atPole: false }; }
    const planet = planets[p.planetIndex];
    if (!planet) return null;
    const c = worldToMinimap(planet.position.x, planet.position.z, bounds, size, padding);
    return surfaceDotOnDisc(c.x, c.y, worldRadiusToPixels(planet.radius, bounds, size, padding), p.surfaceDir);
  };

  // 5. Intel (fog mode only): ghosts of enemies seen by probes / flyovers / LOS, on the rim of the planet
  //    they were seen on, team-coloured, fading with age and dashed once stale; our own spotted units in red.
  if (fogCam && fog[viewerIndex]) {
    const viewerFog = fog[viewerIndex];
    for (const ghost of viewerFog.ghosts) {
      const gPlayer = players.find(p => p.id === ghost.playerId);
      if (!gPlayer) continue;
      if (visibleEnemyIds.has(ghost.playerId)) continue;            // seen right now: drawn as a unit below
      const color = TEAMS[gPlayer.teamIndex % TEAMS.length].primary;
      const turnsAgo = Math.max(0, gameState.turnNumber - ghost.turnSeen);
      const pi = nearestPlanetIndex(ghost.position, planets);
      let gx: number, gy: number;
      if (pi >= 0) {
        const planet = planets[pi];
        const c = worldToMinimap(planet.position.x, planet.position.z, bounds, size, padding);
        const dir = { x: ghost.position.x - planet.position.x, y: ghost.position.y - planet.position.y, z: ghost.position.z - planet.position.z };
        const d = surfaceDotOnDisc(c.x, c.y, worldRadiusToPixels(planet.radius, bounds, size, padding), dir);
        gx = d.x; gy = d.y;
      } else {
        const q = worldToMinimap(ghost.position.x, ghost.position.z, bounds, size, padding); gx = q.x; gy = q.y;
      }
      ctx.save();
      if (turnsAgo >= GHOST_STALE_TURNS) ctx.setLineDash([2, 2]);
      ctx.beginPath();
      ctx.arc(gx, gy, 4, 0, Math.PI * 2);
      ctx.strokeStyle = hexToRgba(color, ghostAlpha(turnsAgo));
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.restore();
    }
    for (const ex of viewerFog.exposures) {
      const turnsAgo = Math.max(0, gameState.turnNumber - ex.turnSeen);
      if (turnsAgo >= GHOST_STALE_TURNS) continue;
      const q = worldToMinimap(ex.position.x, ex.position.z, bounds, size, padding);
      ctx.save();
      ctx.setLineDash([2, 2]);
      ctx.beginPath();
      ctx.arc(q.x, q.y, 6, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(255, 80, 60, ${ghostAlpha(turnsAgo).toFixed(2)})`;
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.restore();
    }
  }

  // 6. Players
  const viewer = players[viewerIndex];
  const viewerTeam = viewer ? viewer.teamIndex : -1;

  for (const p of players) {
    if (!p.alive) continue;
    // Fog filter: in fog mode, only show teammates + visible enemies
    if (fogCam) {
      if (p.teamIndex !== viewerTeam && !visibleEnemyIds.has(p.id)) continue;
    }
    const dot = unitDot(p);
    if (!dot) continue;
    const color = TEAMS[p.teamIndex % TEAMS.length].primary;
    const isViewer = (p.id === (viewer ? viewer.id : -1));
    const isTeammate = p.teamIndex === viewerTeam;
    const isActing = players[gameState.currentPlayerIndex]?.id === p.id;
    const r = isViewer ? 4.5 : 3;
    // dark halo so a dot reads against any planet colour
    ctx.beginPath();
    ctx.arc(dot.x, dot.y, r + 1.5, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.fill();
    ctx.beginPath();
    ctx.arc(dot.x, dot.y, r, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
    if (dot.atPole) {                                   // at a pole there is no compass direction: mark it
      ctx.beginPath(); ctx.arc(dot.x, dot.y, r + 3, 0, Math.PI * 2);
      ctx.strokeStyle = hexToRgba(color, 0.5); ctx.lineWidth = 1; ctx.setLineDash([1, 2]); ctx.stroke(); ctx.setLineDash([]);
    }
    if (isViewer) {
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.95)';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      // outward heading tick: "you are here, facing out from the planet"
      const planet = planets[p.planetIndex];
      if (planet && !p.airborne) {
        const c = worldToMinimap(planet.position.x, planet.position.z, bounds, size, padding);
        const ox = dot.x - c.x, oy = dot.y - c.y, ol = Math.hypot(ox, oy) || 1;
        ctx.beginPath(); ctx.moveTo(dot.x + (ox / ol) * (r + 1), dot.y + (oy / ol) * (r + 1)); ctx.lineTo(dot.x + (ox / ol) * (r + 7), dot.y + (oy / ol) * (r + 7));
        ctx.strokeStyle = 'rgba(255,255,255,0.9)'; ctx.lineWidth = 1.5; ctx.stroke();
      }
    } else if (isTeammate) {
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.45)';
      ctx.lineWidth = 1;
      ctx.stroke();
    } else {
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';   // a currently visible enemy: solid dot, faint rim
      ctx.lineWidth = 1;
      ctx.stroke();
    }
    if (isActing && isTeammate && !isViewer) {         // the teammate whose turn it is pulses
      const pulse = 6 + Math.sin(now * 4) * 1.5;
      ctx.beginPath(); ctx.arc(dot.x, dot.y, pulse, 0, Math.PI * 2);
      ctx.strokeStyle = hexToRgba(color, 0.7); ctx.lineWidth = 1; ctx.stroke();
    }
  }

  // 7. Projectile
  if (projectile) {
    const pos = worldToMinimap(projectile.pos.x, projectile.pos.z, bounds, size, padding);
    const wepDef = WEAPONS[projectile.weaponKey];
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, 2, 0, Math.PI * 2);
    ctx.fillStyle = wepDef ? wepDef.color : '#ffffff';
    ctx.fill();
  }

  // 8. Camera direction indicator
  if (viewer && viewer.alive) {
    const vdot = unitDot(viewer);
    if (vdot) {
      const pos = vdot;
      // cameraTheta: horizontal angle around target (radians). 0 = +Z direction in Three.js spherical coords.
      // In XZ minimap, +Z is down. Camera looks FROM theta direction, so indicator points opposite.
      const angle = -cameraTheta + Math.PI; // point in look direction on minimap
      const triLen = 7;
      ctx.save();
      ctx.translate(pos.x, pos.y);
      ctx.rotate(angle);
      ctx.beginPath();
      ctx.moveTo(0, -triLen);
      ctx.lineTo(-3, triLen * 0.4);
      ctx.lineTo(3, triLen * 0.4);
      ctx.closePath();
      ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
      ctx.fill();
      ctx.restore();
    }
  }
}

// ---------------------------------------------------------------------------
// Drawing helpers
// ---------------------------------------------------------------------------

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}


function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
